import React from 'react';
import { Megaphone } from 'lucide-react';

/**
 * De dónde viene un lead: su PROCEDENCIA. Una campaña de un tratamiento
 * («Campaña <tratamiento>») o, aparte, una de las demás vías (web, correo,
 * recomendación, llamada, otros).
 *
 * Quién la decide es el servidor (services/procedencia_lead.py, UNA función:
 * lo puesto a mano; si no, «de dónde viene»; si no, el correo de una marca);
 * aquí solo se pinta. Las CAMPAÑAS van en ámbar y con un megáfono, para que
 * se vean de un vistazo; las demás vías, en gris; lo que no se sabe («Sin
 * procedencia»), con borde discontinuo, como «Sin identificar» en la marca.
 *
 * Las opciones para ponerla a mano las manda el servidor (`procedencias` de
 * la lista), que es quien tiene el catálogo de la clínica. Aquí no hay una
 * copia de respaldo: sin opciones, el desplegable solo ofrece la vacía (y la
 * que ya tuviera el lead), en vez de inventarse campañas que la clínica no
 * tiene.
 */
export const SIN_OPCIONES_PROCEDENCIA = { campanas: [], vias: [] };

export const SIN_PROCEDENCIA = 'Sin procedencia';

export const esCampana = (clave) => String(clave || '').startsWith('campana:');

const ESTILO = {
  campana: 'bg-amber-50 text-amber-900 border-amber-300',
  via: 'bg-slate-50 text-slate-700 border-slate-300',
  sin: 'bg-transparent text-muted-foreground border-dashed border-gray-400',
};

/** El estilo de una procedencia (por su clave): campaña, vía o sin. */
export function estiloProcedencia(clave) {
  if (!clave || clave === 'sin') return ESTILO.sin;
  return esCampana(clave) ? ESTILO.campana : ESTILO.via;
}

/**
 * La etiqueta. `inline-flex` y `no-underline` a propósito, como la marca: el
 * tachado de la fila de un contactado no la atraviesa.
 */
export default function ProcedenciaLead({ procedencia, testid, title, className = '' }) {
  if (!procedencia) return null;
  const campana = esCampana(procedencia.clave);
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5
      text-xs font-medium no-underline ${estiloProcedencia(procedencia.clave)} ${className}`}
      data-testid={testid} data-procedencia={procedencia.clave || 'sin'}
      title={title || procedencia.explicacion || undefined}>
      {campana ? <Megaphone className="w-3 h-3" aria-hidden="true" /> : null}
      {procedencia.nombre || SIN_PROCEDENCIA}
    </span>
  );
}

/**
 * El desplegable para ponerla a mano: las campañas y las demás vías, en dos
 * grupos. `actual` ({clave, nombre}) se añade si ya no está entre las
 * opciones (una que se puso a mano y ya no está en el catálogo), para que se
 * vea lo que hay.
 */
export function SelectorProcedencia({
  id, value, onChange, opciones, vacio, testid, disabled, actual, className = 'mt-1 block w-full',
}) {
  const ops = opciones?.campanas && opciones?.vias ? opciones : SIN_OPCIONES_PROCEDENCIA;
  const todas = [...ops.campanas, ...ops.vias];
  const falta = actual?.clave && value === actual.clave && !todas.some((o) => o.clave === actual.clave);
  return (
    <select id={id} value={value || ''} data-testid={testid} disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} h-9 rounded-md border border-input bg-background px-3 text-sm`}>
      <option value="">{vacio}</option>
      {falta ? <option value={actual.clave}>{actual.nombre}</option> : null}
      {ops.campanas.length ? (
        <optgroup label="Campañas">
          {ops.campanas.map((o) => <option key={o.clave} value={o.clave}>{o.nombre}</option>)}
        </optgroup>
      ) : null}
      {ops.vias.length ? (
        <optgroup label="Otras vías">
          {ops.vias.map((o) => <option key={o.clave} value={o.clave}>{o.nombre}</option>)}
        </optgroup>
      ) : null}
    </select>
  );
}
